import { formatHrBookingRef } from "./bookingRefDisplay";
import {
  formatHrLabel,
  isLikelyOpaqueLdsDealToken,
} from "./resolveCustomerFacingOfferNumber";
import { pickOrganizedTourDealSpread } from "./pickOrganizedTourDealSpread";

/** Long numeric LDS / supplier deal tokens — never show as "מספר הצעה" (8-digit HR refs are OK). */
export function isLikelyLdsOrSupplierDealId(value) {
  const t = String(value ?? "").trim();
  if (!t) return false;
  if (isLikelyOpaqueLdsDealToken(t)) return true;
  if (/^HR-[1-9]\d{7}$/i.test(t.replace(/\s/g, ""))) return false;
  if (/^\d{8}$/.test(t)) return false;
  if (/^\d{10,}$/.test(t)) return true;
  if (/^\d{9}$/.test(t)) return true;
  return false;
}

function normalizeHrDisplayValue(raw) {
  const t = String(raw ?? "").trim();
  if (!t || isLikelyLdsOrSupplierDealId(t)) return null;
  const hr = formatHrBookingRef(t) || formatHrLabel(t);
  if (hr) return hr;
  const compact = t.replace(/\s/g, "");
  if (/^HR-[1-9]\d{7}$/i.test(compact)) {
    return compact.replace(/^hr-/i, "HR-");
  }
  return null;
}

function pickNested(obj, path) {
  let cur = obj;
  for (const key of path) {
    if (!cur || typeof cur !== "object") return null;
    cur = cur[key];
  }
  return cur;
}

function traceHrResolve(dealId, payload) {
  if (!import.meta.env?.DEV) return;
  console.log("[ORG_TOUR_HR_CODE_RESOLVE]", { dealId, ...payload });
}

/**
 * HR / booking_ref only — never LDS dealId / ldsDealId (resolveCustomerFacingOfferNumber deal fallback excluded).
 */
export function resolveOrganizedTourSearchDisplayNumber(deal) {
  const root = pickOrganizedTourDealSpread(deal);
  const dealId = String(root.dealId || root.ldsDealId || root.dealid || "").trim();
  const supplierId = String(
    root.supplierDetails?.supplierId || root.supplierId || "",
  ).trim();

  const blockedCandidate = pickNested(root, ["dealId"]) || pickNested(root, ["ldsDealId"]);
  if (blockedCandidate && isLikelyLdsOrSupplierDealId(blockedCandidate)) {
    if (import.meta.env?.DEV) {
      console.log("[ORG_TOUR_SUPPLIER_ID_BLOCKED_AS_DISPLAY_ID]", {
        dealId,
        field: "dealId/ldsDealId",
        value: String(blockedCandidate),
        supplierId: supplierId || null,
      });
    }
  }

  const fieldPaths = [
    ["booking_ref_display"],
    ["bookingRefDisplay"],
    ["booking_ref"],
    ["bookingRef"],
    ["hrCode"],
    ["hr_code"],
    ["hotelsRiverCode"],
    ["hotels_river_code"],
    ["displayOfferNumber"],
    ["display_offer_number"],
    ["ourPrice", "customer_quote_display", "offer_number"],
    ["ourPrice", "customerQuoteDisplay", "offerNumber"],
    ["ourprice", "customer_quote_display", "offer_number"],
    ["customer_quote_display", "offer_number"],
    ["customerQuoteDisplay", "offerNumber"],
  ];

  for (const path of fieldPaths) {
    const raw = pickNested(root, path);
    if (raw == null) continue;
    const hr = normalizeHrDisplayValue(raw);
    if (hr) {
      const out = {
        show: true,
        value: hr,
        label: "מספר הצעה",
        source: path.join("."),
      };
      traceHrResolve(dealId, out);
      return out;
    }
    if (isLikelyLdsOrSupplierDealId(raw)) {
      if (import.meta.env?.DEV) {
        console.log("[ORG_TOUR_SUPPLIER_ID_BLOCKED_AS_DISPLAY_ID]", {
          dealId,
          field: path.join("."),
          value: String(raw),
        });
      }
    }
  }

  const out = { show: false, value: "", label: "מספר הצעה", source: "none" };
  traceHrResolve(dealId, { ...out, note: "no_hr_fields_on_search_row" });
  return out;
}
